import { GCInstant } from './gcinstant';
import { isNativeSupportLineLoginChannel } from 'src/conf/env';
import { isPlatformNative } from './native/native.util';
import StateObserver from 'src/StateObserver';
import features from 'src/replicant/ruleset/features';
import { openPopupPromise } from './popups/popupOpenClose';
import { track } from './analytics/utils';

/**
 * Kill-switch for the forced native app update flow.
 * Set to false to disable forcing legacy native clients to update.
 */
const FORCE_NATIVE_APP_UPDATE = true;

/**
 * Runs after the entry finale.
 *
 * The LIFF → MiniApp migration is complete, so the only remaining step is to
 * force old native clients (that can only use the legacy LINE game channel) to
 * update to a build that supports the LINE login channel.
 */
export async function miniAppMigrationAfterGameStart() {
  // Skip guest mode — no login or social features
  if (features(StateObserver.getState().user).isGuestMode) {
    return;
  }

  // Force old native clients onto a build that supports the LINE login channel
  if (FORCE_NATIVE_APP_UPDATE && shouldForceNativeAppUpdate()) {
    await forceNativeAppUpdate(); // It will block next code execution
  }
}

/**
 * Determines whether the current native app session should be forced to update native app
 *
 * @returns boolean indicating if a forced native app update is necessary
 */
function shouldForceNativeAppUpdate() {
  if (!isPlatformNative()) return false;

  // Check if user native login api can use LINE login channel
  // If it return false we have only GAME channel, and should force update
  const canUseNativeLINELoginChannel = isNativeSupportLineLoginChannel(
    GCInstant.legacyNative,
  );

  return !canUseNativeLINELoginChannel;
}

/**
 * Forces the user to update the native app and halts further mini-app execution.
 *
 * This function never resolves (the returned promise is intentionally left pending)
 * after initiating the update flow. Callers should treat this as a terminal operation.
 *
 * Side effects:
 * - UI UpdateApp popup is shown.
 * - External navigation to the app store is triggered.
 *
 * IMPORTANT: Do not place code after the invocation of this function expecting it to run.
 *
 * @returns A promise that never resolves; awaiting it will block indefinitely.
 */
async function forceNativeAppUpdate() {
  // Dismiss any loading screens.
  await GCInstant.dismissLoadingScreenAsync({
    launchPath: 'force_native_app_update',
  });

  // Track the force update event
  track('NativeForceUpdatePopup', {});

  // Show error popup to inform user about the need to update native app
  await openPopupPromise('popupUpdateApp', {
    hardUpdate: true,
    origin: 'launch',
    additionalTracking: {
      feature: 'miniapp_migration',
      $subFeature: 'force_native_app_update_popup',
    },
  });
  // Wait indefinitely to prevent further execution
  await new Promise(() => {
    // Intentionally empty
  });
}
